import { Avatar, Box, Grid, Stack, styled } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IConsultant, IConsultantSearchFilterDTO } from 'wisestep-api';
import {
  BottomActionBar,
  WsButton,
  WsColors,
  WsDrawer,
  WsPagination,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import ActiveConsultantCard from '../../components/ActiveConsultantCard';
import JobPostersBulkEmail, { mailType } from '../../components/BulkEmail/JobPostersBulkEmail';
import { ConsultantsAdvancedFilter } from '../../components/filters/consultantsFilter';
import NoDataFoundCard from '../../components/noDataFoundCard';
import TalentPoolListSideNav from '../candidates/talentpool/components/talentpoolListSideNav';
import { activeConsultantsQuery } from '../importConsultants/helper/EmptyConsultantFilter';
import useConsultantsListing from '../importConsultants/listing/hooks/useConsultantsListing';

import ActiveConsultantInfo from './components/activeConsultantInfo/ActiveConsultantInfo';
import ActiveConsultantsSearchFilter from './components/advancedFilter/ActiveConsultantsSearchFilter';
import { ConsultantHeader } from './components/consultantHeader.tsx';
import { constructActiveConsultantsQuery } from './helper/constructActiveConsultantsQuery';
import { IConsultantsAdvancedFilter, IConsultantsSearchFilter } from './types/IConsultantsSearch';

const LayoutTheme = styled(Box)(() => ({
  maxWidth: '100%',
  maxHeight: 'xs',
  backgroundColor: WsColors.TertiaryShades50,
  padding: 2,
}));
const selectedIndex = (index: number, value: string) => console.log(index, value);

const ActiveConsultantsPage = () => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [query, setQuery] = useState<IConsultantSearchFilterDTO>(activeConsultantsQuery);
  const [loader, setLoader] = useState<boolean>(false);
  const [consultants, totalConsultants] = useConsultantsListing(query, setLoader);
  const [openCommunication, setOpenCommunication] = useState<boolean>(false);
  const [openConsultantDetails, setOpenDetails] = useState<boolean>(false);
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const [selectedConsultants, setSelectedConsultants] = useState<
    (IConsultant & {
      isSelected: boolean;
    })[]
  >([]);
  const [selectedConsultant, setSelectedConsultant] = useState<IConsultant>();
  const methods = useForm<IConsultantsSearchFilter & IConsultantsAdvancedFilter>({
    defaultValues: {
      willingToRelocate: undefined,
      skills: [],
    },
  });

  const { reset, handleSubmit } = methods;

  const performSearch = (data: IConsultantsSearchFilter & IConsultantsAdvancedFilter) => {
    setQuery(constructActiveConsultantsQuery(data, query));
    setOpenAdvancedFilters(false);
  };

  const onReset = () => {
    reset();
    handleSubmit((data) => performSearch(data))();
  };

  const pageChange = (event, value) => {
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setQuery(updatedQuery);
    setPageNumber(value + 1);
  };

  const addSelectedConsultant = (consultant: IConsultant & { isSelected: boolean }) => {
    consultant.isSelected = !consultant.isSelected;
    if (!selectedConsultants.some((existing) => existing.id === consultant.id))
      selectedConsultants.push(consultant);
    else selectedConsultants.splice(selectedConsultants.indexOf(consultant), 1);
    setSelectedConsultants([...selectedConsultants]);
  };

  const unSelectAllConsultants = () => {
    consultants.forEach((consultant) => {
      consultant.isSelected = false;
    });
    setSelectedConsultants([]);
  };

  const selectAllConsultants = () => {
    consultants.forEach((consultant) => {
      if (!consultant.isSelected) selectedConsultants.push(consultant);
      consultant.isSelected = true;
    });
    setSelectedConsultants([...consultants]);
  };

  const askForResume = (consultant: IConsultant & { isSelected: boolean }) => {
    addSelectedConsultant(consultant);
    setOpenCommunication(true);
  };

  const getToEmailList = () => {
    const arrayOfEmails = selectedConsultants.map((consultant) => consultant.ownerId ?? '');

    const uniqueArrayOfEmails = Array.from(new Set(arrayOfEmails));

    const maskedEmails: {
      email: string;
      text: string;
    }[] = uniqueArrayOfEmails.map((field) => {
      const masked = field.substring(0, 2) + 'xxxx@xxxxx' + field.substring(field.lastIndexOf('.'));
      return {
        email: field ? field : 'NotAvailable',
        text: masked,
      };
    });

    return maskedEmails;
  };

  return (
    <FormProvider {...methods}>
      <LayoutTheme>
        <ConsultantHeader />
        <Grid container pt={2} spacing={2}>
          <Grid item xs={3}>
            <TalentPoolListSideNav selectedIndexValue={selectedIndex} />
          </Grid>
          <Grid item xs={9}>
            <ActiveConsultantsSearchFilter
              setOpenAdvancedFilters={setOpenAdvancedFilters}
              performSearch={performSearch}
              onReset={onReset}
            />

            <ConsultantsAdvancedFilter
              open={openAdvancedFilters}
              onApplyFilters={performSearch}
              filterClose={() => setOpenAdvancedFilters(false)}
              onReset={onReset}
            />

            <Box>
              <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                {totalConsultants > 0 && (
                  <WsPagination
                    count={Math.ceil(totalConsultants / 20)}
                    onChange={pageChange}
                    itemsPerPage={1}
                    pageNumber={query.page}
                    defaultPage={pageNumber}
                    isPageCount={false}
                    type="Consultants"
                    totalCount={totalConsultants}
                  />
                )}
              </Grid>
              <Box>
                {isEmpty(consultants) ? (
                  <NoDataFoundCard text="No Data found" />
                ) : (
                  consultants.length &&
                  !loader &&
                  consultants?.map((consultant, index) => (
                    <Box my={2} key={index}>
                      <ActiveConsultantCard
                        consultant={consultant}
                        isCheckBox={true}
                        handleCheckBox={() => addSelectedConsultant(consultant)}
                        askForResume={() => askForResume(consultant)}
                        isSelected={
                          selectedConsultants.find((existing) => existing.id === consultant.id)
                            ?.isSelected ?? false
                        }
                        onViewDetails={() => {
                          setSelectedConsultant(consultant);
                          setOpenDetails(true);
                        }}
                      />
                    </Box>
                  ))
                )}
              </Box>
              <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                {totalConsultants > 0 && (
                  <WsPagination
                    count={Math.ceil(totalConsultants / 20)}
                    onChange={pageChange}
                    itemsPerPage={20}
                    pageNumber={query.page}
                    defaultPage={pageNumber}
                    isPageCount={false}
                    type="Consultants"
                    totalCount={totalConsultants}
                  />
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <WsDrawer
          open={openCommunication}
          title="Select suitable Consultants for Job(s)"
          style={{ zIndex: '1300' }}
          onClose={() => null}
        >
          <JobPostersBulkEmail
            toEmailList={getToEmailList()}
            consultants={selectedConsultants}
            open={true}
            handleClose={() => {
              unSelectAllConsultants();
              setOpenCommunication(false);
            }}
            template={''}
            jobs={[]}
            type={mailType.ACTIVECONSULTANT}
          />
        </WsDrawer>

        {openConsultantDetails && (
          <WsDrawer
            open={openConsultantDetails}
            title="Select suitable Consultants for Job(s)"
            style={{ zIndex: '1300' }}
            onClose={() => {
              setSelectedConsultant(undefined);
              setOpenDetails(false);
            }}
          >
            <ActiveConsultantInfo consultantData={selectedConsultant} />
          </WsDrawer>
        )}

        {!isEmpty(selectedConsultants) && (
          <BottomActionBar>
            <Grid item container justifyContent="space-between" px={6}>
              <Grid item container xs={9} display="flex">
                <Grid item pr={2} display="flex">
                  <Avatar
                    variant="square"
                    sx={{ bgcolor: WsColors.TheBlues400, width: 24, height: 24 }}
                  >
                    <WsTypography color={WsTypoColors.WHITE}>
                      {selectedConsultants.length}
                    </WsTypography>
                  </Avatar>
                  <WsTypography color={WsTypoColors.PRIMARY}>Consultant(s) Selected</WsTypography>
                </Grid>

                <Stack spacing={2} direction="row">
                  <WsButton onClick={selectAllConsultants}>Select All</WsButton>
                  <WsButton onClick={unSelectAllConsultants}>Un-Select All</WsButton>
                </Stack>
              </Grid>

              <WsButton onClick={() => setOpenCommunication(true)}>Apply</WsButton>
            </Grid>
          </BottomActionBar>
        )}
      </LayoutTheme>
    </FormProvider>
  );
};

export default ActiveConsultantsPage;
